import React, {useMemo, useState} from 'react';
import {ButtonDropdownField, DropdownList, Modal, SubHeading, Text} from '@forecast-it/design-system';
import {FormattedHTMLMessage, FormattedMessage, useIntl} from 'react-intl';
import {Deliverable, DeliverableEdges} from './BaselineTypes';

type ChangeDeliverableModalProps = {
	closeModal: () => void;
	callback: (selectedDeliverable: Deliverable) => void;
	projectDeliverables: DeliverableEdges;
	currentDeliverable?: Deliverable;
};

const ChangeDeliverableModal = ({
	closeModal,
	callback,
	projectDeliverables,
	currentDeliverable,
}: ChangeDeliverableModalProps): React.JSX.Element => {
	const {formatMessage} = useIntl();

	const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);

	const onClickConfirm = () => {
		if (!selectedDeliverable) {
			return;
		}
		callback(selectedDeliverable);
		closeModal();
	};

	const unSelectedDeliverables = useMemo(
		() =>
			currentDeliverable
				? projectDeliverables.filter(
						deliverable => ![selectedDeliverable?.id, currentDeliverable.id].includes(deliverable?.node?.id)
				  )
				: projectDeliverables,
		[projectDeliverables, selectedDeliverable, currentDeliverable]
	);

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={'deliverable.change_deliverable'} />
			</Modal.Title>
			<Modal.Content>
				<FormattedHTMLMessage id={'deliverable.change_deliverable.description'} />
				<SubHeading>{formatMessage({id: 'deliverable.change_deliverable.current_selected'})}</SubHeading>
				<Text>{currentDeliverable?.name || formatMessage({id: 'common.none'})}</Text>
				<SubHeading>{formatMessage({id: 'deliverable.select_another_deliverable'})}</SubHeading>
				<DropdownList>
					<DropdownList.Trigger>
						<ButtonDropdownField>
							{!!selectedDeliverable && (
								<Text ellipsis={true} size={'1'}>
									{selectedDeliverable.name}
								</Text>
							)}
						</ButtonDropdownField>
					</DropdownList.Trigger>
					{!!unSelectedDeliverables.length && (
						<DropdownList.Content offset={10} useTriggerWidth>
							{unSelectedDeliverables.map(deliverable => (
								<DropdownList.Item onClick={() => setSelectedDeliverable(deliverable?.node || null)}>
									{deliverable?.node?.name}
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					)}
				</DropdownList>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={onClickConfirm}
					dataCy={'modal-btn-confirm'}
					disabled={!selectedDeliverable}
				>
					<FormattedMessage id="common.confirm" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id="common.go_back" />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ChangeDeliverableModal;
